import { formatDeviceName } from '../util/deviceName';
import endpoint from '../util/endpoint';
import CPromise from '@whiz-cart/node-shared/cPromise';
import type { CustomerLabel } from '@whiz-cart/node-shared/models/customer/customerLabel';
import { Payment } from '@whiz-cart/node-shared/models/payment/payment';
import { SessionScaleTrigger, SessionSoftwareTara, SessionWeightDeviation } from '@whiz-cart/node-shared/models/session/activeDevice';
import type {
    BasketItemChangeModel,
    SessionChangesModel,
    WeightWithoutScanImagesModel,
} from '@whiz-cart/node-shared/models/session/changes';
import type { Session } from '@whiz-cart/node-shared/models/session/session';
import { type CartImageView, cartImageViewUrl } from '@whiz-cart/node-shared/models/snapshot/cartImageView';
import { CustomerTicket } from '@whiz-cart/node-shared/models/ticket/ticket';
import { createStore } from 'cross-state';
import _, { mapValues } from 'lodash';

export interface SessionMonitorDetailsSession extends Session {
    history?: any[];
    isSuspicious?: boolean;
}

export type FetchedSession = Session & {
    storeGuid?: string;
    sessionGuid?: string;
    history?: SessionChangesModel;
    deviceId?: string;
};

export const sessionMonitorDetailsStore = createStore<Record<string, SessionMonitorDetailsSession>>({});

const processBasketChange = (item: any) => {
    const {
        delta,
        basketItem: { basketItemGuid },
        modifiedOn,
        createdOn,
        changeGuid,
    } = item;
    if (delta === 0) return [];

    return {
        ...item,
        id: `${basketItemGuid}:${changeGuid}`,
        type: 'basketChange',
        time: modifiedOn ?? createdOn,
        receivedOn: new Date(),
        images: item.basketItem?.basketMetadataList?.find((x: any) => x.correlationGuid === item.changeGuid)?.images,
    };
};

const processTicketChange = (item: any) => {
    const { closedOn, id, openedOn } = item;

    const history = [];
    history.unshift({
        ...item,
        status: 0,
        historyId: `${id}:opened`,
        type: 'ticketChange',
        time: openedOn,
    });

    if (closedOn) {
        history.unshift({
            ...item,
            historyId: `${id}:closed`,
            type: 'ticketChange',
            time: closedOn,
        });
    }

    return history;
};

const processTaraChange = (item: any) => {
    const { createdOn } = item;

    return {
        ...item,
        id: `tara:${createdOn}`,
        type: 'tara',
        time: createdOn,
    };
};

const processScaleTrigger = (item: any) => {
    const { createdOn } = item;

    return {
        ...item,
        id: `scaleTrigger:${createdOn}`,
        type: 'scaleTrigger',
        time: createdOn,
    };
};

/**
 * @param images Resolved image URLs for this alert. Store-manager resolves them — on the live notification as
 * `payload.images`, and on a fetched session in the session-changes response — so this screen never builds a cart
 * image URL and needs no knowledge of the storage generation. Undefined when the alert stored no images, or when
 * the store's session-monitor snapshot gate suppressed them.
 */
const processWeightWithoutScan = (item: any, images?: WeightWithoutScanImagesModel) => {
    return {
        ...item,
        ...item.model,
        snapshotUrl: images?.snapshotUrl,
        snapshotBeforeEventUrl: images?.snapshotBeforeEventUrl,
        id: `weightWithoutScan:${item?.model?.createdOn ?? item?.createdOn}`,
        type: 'weightWithoutScan',
        time: item?.model?.createdOn ?? item?.createdOn,
    };
};

const processWeightDeviation = (item: any) => {
    const { createdOn } = item;

    return {
        ...item,
        id: `weightDeviation:${createdOn}`,
        type: 'weightDeviation',
        time: createdOn,
    };
};

const processSessionEvents = (session: any) => {
    const items = [];

    items.push({
        ...session,
        id: 'sessionStart',
        type: 'sessionStart',
        time: session.startedOn,
        ...session.history.sessionStartImages,
    });

    if (session.endedOn) {
        items.push({
            ...session,
            id: 'sessionEnd',
            type: 'sessionEnd',
            time: session.endedOn,
            ...session.history.sessionEndImages,
        });
    }

    for (const paymentRequest of session.history.paymentRequests) {
        items.push({
            ...paymentRequest,
            id: `${paymentRequest.paymentRequestGuid}:start`,
            type: 'paymentStart',
            time: paymentRequest.createdOn,
        });

        if (paymentRequest.completedOn || paymentRequest.status > 2) {
            items.push({
                ...paymentRequest,
                id: `${paymentRequest.paymentRequestGuid}:end`,
                type: 'paymentEnd',
                time: paymentRequest.completedOn ?? paymentRequest.modifiedOn,
            });
        }
    }

    return items;
};

const processAsyncAvailableImages = (asyncAvailableImages: any, image: CartImageView) => {
    const result = { ...asyncAvailableImages };
    result[image.logicalPath] = cartImageViewUrl(image);
    return result;
};

const update = (sessionGuid: string, updateSession: (session: any) => any, upsert?: boolean) =>
    sessionMonitorDetailsStore.set((state) => {
        if (!state[sessionGuid] && !upsert) return state;

        const session = updateSession(state[sessionGuid]);

        session.deviceId = formatDeviceName(session.isMobile ? String(session.sequence) : session.cartId, session.isMobile);

        session.history = _(session.history)
            .orderBy('receivedOn', 'desc')
            .uniqBy('id')
            .orderBy('time', 'desc')
            .map((item) => {
                if (item.images) {
                    const availableImages = _.uniq(
                        (item.availableImages ?? []).concat(
                            item.images.map((path: any) => session.asyncAvailableImages?.[path]).filter(Boolean),
                        ),
                    );

                    item = { ...item, availableImages };
                }

                return item;
            })
            .value();

        session.items = _(session.history)
            .filter((item) => item.type === 'basketChange')
            .orderBy('time', 'desc')
            .map('basketItem')
            .uniqBy('basketItemGuid')
            .orderBy('createdOn', 'desc')
            .value();

        return { ...state, [sessionGuid]: session };
    });

export const setSessionLoading = (sessionGuid: string, loading: boolean) =>
    sessionMonitorDetailsStore.set((state) => ({ ...state, [sessionGuid]: { ...state[sessionGuid], isLoading: loading } as any }));

export const updateSession = (payload: FetchedSession & Record<string, any>) => {
    const resolvedWwsImages = new Map<string, WeightWithoutScanImagesModel>(
        (payload.history?.weightWithoutScanAlerts ?? []).map((images: WeightWithoutScanImagesModel) => [images.modelGuid, images]),
    );
    const history: any = payload.history;
    const entries = [].concat(
        ...history.basketChanges.map(processBasketChange),
        ...history.tickets.map(processTicketChange),
        ...history.taras.map(processTaraChange),
        ...(history.scaleTriggers?.map(processScaleTrigger) ?? []),
        ...(payload.weightWithoutScanAlerts?.map((alert: any) =>
            processWeightWithoutScan(alert, resolvedWwsImages.get(alert?.modelGuid)),
        ) ?? []),
        ...(history.weightDeviations?.map(processWeightDeviation) ?? []),
        ...processSessionEvents(payload),
    );
    const isSuspicious = payload.customer?.labels?.includes('IsSuspicious');

    update(
        payload.sessionGuid ?? '',
        (s) => ({
            ...s,
            ...payload,
            history: !isSuspicious ? entries.filter((t: any) => t?.category !== 'taggedCustomer') : entries,
            manualCheck: payload.manualCheck,
            customer: payload.customer,
            tickets: !isSuspicious ? history?.tickets?.filter((t: any) => t?.category !== 'taggedCustomer') : history?.tickets,
            isLoading: false,
        }),
        true,
    );
};

export const fetchSession = (storeGuid: string, sessionGuid: string) =>
    new CPromise<void>(async (resolve, reject, onCancel) => {
        let isCanceled = false;
        onCancel(() => (isCanceled = true));

        try {
            const [session, history] = await Promise.all([
                endpoint('storeManager.getSessionBasket', { storeGuid, sessionGuid }).get<Session>(),
                endpoint('storeManager.getSessionHistory', { storeGuid, sessionGuid }).get<SessionChangesModel>(),
            ]);
            if (!isCanceled) updateSession({ storeGuid, sessionGuid, ...session, history });
            resolve();
        } catch (error) {
            // Drops every other loaded session, as the redux reducer this replaced did
            if (!isCanceled) sessionMonitorDetailsStore.set({ [sessionGuid]: { sessionGuid, error, isLoading: false } as any });
            reject(error);
        }
    });

export const addBasketChange = (payload: BasketItemChangeModel & Record<string, any>) =>
    update(payload.basketItem?.sessionGuid ?? '', (s) => {
        if (payload.oldAmount === null && payload.newAmount === 0) {
            // item is unknown barcode that was automatically removed by the system
            // we look for an entry with the same changeGuid and remove it, since a separate "unknown barcode" event is generated
            // if the user decides to keep the unknown barcode item, a new entry will be generated
            const history = [...s.history];
            const index = history.findIndex((entry) => entry.changeGuid === payload.changeGuid);
            if (index !== -1) {
                history.splice(index, 1);
                return { ...s, history };
            }
        }

        return { ...s, history: s.history.concat(processBasketChange(payload)) };
    });

export const addTicketChange = (payload: CustomerTicket) =>
    update(payload.sessionGuid ?? '', (s) => ({
        ...s,
        history: s.history.concat(processTicketChange(payload)),
        tickets: s.tickets.filter((t: any) => t.id !== payload.id).concat(payload),
    }));

export const addTaraChange = (payload: SessionSoftwareTara & Record<string, any>) =>
    update(payload.sessionGuid ?? '', (s) => ({ ...s, history: s.history.concat(processTaraChange(payload.tara)) }));

export const addImage = (payload: CartImageView) =>
    update(payload.sessionGuid, (s) => ({ ...s, asyncAvailableImages: processAsyncAvailableImages(s.asyncAvailableImages, payload) }));

export const sessionScaleTrigger = (payload: SessionScaleTrigger & Record<string, any>) =>
    update(payload.sessionGuid ?? '', (s) => ({ ...s, history: s.history.concat(processScaleTrigger(payload)) }));

export const sessionWeightWithoutScan = (payload: any) =>
    update(payload.sessionGuid, (s) => ({ ...s, history: s.history.concat(processWeightWithoutScan(payload, payload.images)) }));

export const sessionManualCheck = (payload: any) => update(payload.sessionGuid, (s) => ({ ...s, manualCheck: payload }));

export const sessionWeightDeviation = (payload: SessionWeightDeviation & Record<string, any>) =>
    update(payload.sessionGuid ?? '', (s) => ({ ...s, history: s.history.concat(processWeightDeviation(payload)) }));

export const paymentRequestCreated = (payload: Payment & Record<string, any>) =>
    update(payload.sessionGuid ?? '', (s) => ({
        ...s,
        paymentRequests: s.paymentRequests.concat(payload),
        history: s.history.concat({
            ...payload,
            id: `${payload.paymentRequestGuid}:start`,
            type: 'paymentStart',
            time: payload.createdOn,
        }),
    }));

export const paymentRequestChanged = (payload: Payment & Record<string, any>) =>
    update(payload.sessionGuid ?? '', (s) => ({
        ...s,
        paymentRequests: s.paymentRequests.filter((x: any) => x.paymentRequestGuid !== payload.paymentRequestGuid).concat(payload),
        history: s.history.concat({
            ...payload,
            id: `${payload.paymentRequestGuid}:end`,
            type: 'paymentEnd',
            time: payload.completedOn ?? payload.modifiedOn,
        }),
    }));

export const updateSessionWeights = (payload: { sessionGuid: string; actualWeight?: number; expectedWeight?: number; t: any }) =>
    update(payload.sessionGuid, (s) => {
        if (s.weightUpdatedOn > payload.t) return s;

        return {
            ...s,
            actualWeight: payload.actualWeight ?? s.actualWeight,
            expectedWeight: payload.expectedWeight ?? s.expectedWeight,
            weightUpdatedOn: payload.t,
        };
    });

export const endSession = (payload: { sessionGuid: string; endedOn: string; images: unknown }) =>
    update(payload.sessionGuid, (s) => ({
        ...s,
        endedOn: payload.endedOn,
        history: s.history.concat({ id: 'sessionEnd', type: 'sessionEnd', time: payload.endedOn, images: payload.images }),
    }));

export const updateSessionLocations = (payload: { sessionGuid?: string | null; location?: unknown }[]) => {
    for (const { sessionGuid, location } of payload) {
        update(sessionGuid ?? '', (s) => ({ ...s, location }));
    }
};

export const updateSessionCustomerLabels = (customerGuid: string, customerLabels: CustomerLabel[]) => {
    const labels = customerLabels.filter((label) => !label.deletedOn).map((label) => label.label);
    const isSuspicious = labels.includes('IsSuspicious');

    sessionMonitorDetailsStore.set((state) =>
        mapValues(state, (session) => {
            if (session.customer?.userGuid !== customerGuid) {
                return session;
            }

            return {
                ...session,
                customer: { ...session.customer, labels },
                history: !isSuspicious ? session.history?.filter((h: any) => h?.category !== 'taggedCustomer') : session.history,
                tickets: !isSuspicious ? session.tickets?.filter((t) => t?.category !== 'taggedCustomer') : session.tickets,
            };
        }),
    );
};
